import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme, AKZENTE } from '@/contexts/ThemeContext';
import { useSettings } from '@/contexts/SettingsContext';
import { getNavSectionsForRole, SALES_PATHS, FAVORITEN_STANDARD } from '@/lib/permissions';
import api from '@/lib/api';
import {
  ChevronLeft, ChevronRight, ChevronDown,
  LogOut, Sun, Moon, X, Search, Star
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import AccountSecurityDialog from '@/components/AccountSecurityDialog';
import { DiktatEintrag } from '@/components/Diktat';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const { user, logout, userPermissions, isAdminPermissions, checkAuth } = useAuth();
  const { theme, toggleTheme, akzent, setAkzent } = useTheme();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [salesOpen, setSalesOpen] = useState(() => SALES_PATHS.includes(location.pathname));
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [offen, setOffen] = useState(null);            // welcher Bereich aufgeklappt ist (immer nur einer)
  const [suche, setSuche] = useState(false);
  const [frage, setFrage] = useState('');
  const favSchluessel = `p1_favoriten_${user?.user_id || 'ich'}`;
  const [favPfade, setFavPfade] = useState(() => {
    try { const roh = localStorage.getItem(favSchluessel); if (roh) return JSON.parse(roh); } catch { /* Standard */ }
    return FAVORITEN_STANDARD;
  });

  // Schaltbare Seiten (v8): erst einblenden, wenn das CRM sagt, dass die Funktion an ist
  const [funktionen, setFunktionen] = useState(null);
  useEffect(() => { if (user) api.get('/funktionen').then(r => setFunktionen(r.data)).catch(() => setFunktionen(null)); }, [user]);
  const navSections = getNavSectionsForRole(user?.role, userPermissions, isAdminPermissions, funktionen);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNav = () => {
    if (mobileOpen) onMobileClose();
  };

  const isCollapsed = collapsed && !mobileOpen;
  const isSalesActive = SALES_PATHS.includes(location.pathname);

  const feste = navSections.filter(s => s.fest).flatMap(s => s.items);
  const bereiche = navSections.filter(s => !s.fest);
  const alle = navSections.flatMap(s => s.items);
  const favoriten = favPfade.map(pfad => alle.find(i => i.path === pfad)).filter(i => i && !feste.includes(i));
  const favorit = (pfad) => {
    const liste = favoriten.map(i => i.path);
    const neu = liste.includes(pfad) ? liste.filter(x => x !== pfad) : [...liste, pfad];
    setFavPfade(neu);
    try { localStorage.setItem(favSchluessel, JSON.stringify(neu)); } catch { /* gilt dann bis zum Neuladen */ }
  };
  // der Bereich der offenen Seite: längster passender Pfad gewinnt (/kennzahlen/vertrieb vor /kennzahlen)
  const aktiverBereich = (bereiche
    .flatMap(s => s.items.map(i => ({ titel: s.titel, pfad: i.path })))
    .filter(x => location.pathname === x.pfad || location.pathname.startsWith(x.pfad + '/') || (x.pfad === '/verkauf' && isSalesActive))
    .sort((a, b) => b.pfad.length - a.pfad.length)[0] || {}).titel;
  const treffer = alle.filter(i => i.label.toLowerCase().includes(frage.trim().toLowerCase()));
  const gehe = (pfad) => { setSuche(false); setFrage(''); navigate(pfad); handleNav(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (aktiverBereich) setOffen(aktiverBereich); }, [aktiverBereich]);
  useEffect(() => {
    const taste = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSuche(true); } };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, []);
  const ueberschrift = 'px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 select-none';
  const kennung = (label) => label.toLowerCase().replace(/\s+/g, '-');

  // der Stern zum Anheften — auch am Verkaufsrechner, der Unterpunkte hat (Betreiber 12.10.2026)
  const stern = (item, art, lage) => {
    const istFav = favoriten.includes(item);
    return (
      <button onClick={() => favorit(item.path)} title={istFav ? 'Aus den Favoriten nehmen' : 'Zu den Favoriten'}
        aria-label={`${item.label}: ${istFav ? 'aus den Favoriten nehmen' : 'zu den Favoriten'}`} data-testid={`stern-${art}-${kennung(item.label)}`}
        className={`absolute z-10 ${lage} -translate-y-1/2 rounded-md p-1 transition-opacity focus-visible:opacity-100 group-hover/e:opacity-100 ${istFav && art === 'fav' ? 'opacity-60' : 'opacity-0'}`}>
        <Star className={`w-3.5 h-3.5 ${istFav ? 'fill-primary text-primary' : 'text-muted-foreground'}`} />
      </button>
    );
  };

  const eintrag = (item, art, mitStern = true) => {
    const Icon = item.icon;
    return (
      <div key={`${art}-${item.path}`} className="group/e relative">
        <NavLink to={item.path} end={item.path === '/'} onClick={handleNav} data-testid={`${art}-${kennung(item.label)}`}
          className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 hover:translate-x-0.5 ${
            isActive ? 'bg-primary/10 text-primary p1-marke' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
          <Icon className="w-[18px] h-[18px] flex-shrink-0" />
          <span className="truncate pr-5">{item.label}</span>
        </NavLink>
        {mitStern && stern(item, art, 'right-1.5 top-1/2')}
      </div>
    );
  };

  const symbol = (item) => {
    const Icon = item.icon;
    return (
      <Tooltip key={`sym-${item.path}`}>
        <TooltipTrigger asChild>
          <NavLink to={item.path} end={item.path === '/'} onClick={handleNav} data-testid={`nav-${kennung(item.label)}`}
            className={({ isActive }) => `flex items-center justify-center w-full px-2 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
            <Icon className="w-[18px] h-[18px] flex-shrink-0" />
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">{item.label}</TooltipContent>
      </Tooltip>
    );
  };

  const verkaufsrechner = (item) => {
    const Icon = item.icon;
    return (
      <div key={item.path} data-testid={`nav-group-${kennung(item.label)}`} className="group/e relative">
        {stern(item, 'nav', 'right-7 top-[18px]')}
        <button
          onClick={() => { setSalesOpen(o => !o); if (!isSalesActive) { navigate(item.path); handleNav(); } }}
          className={`relative flex items-center gap-3 w-full px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 hover:translate-x-0.5 ${
            isSalesActive ? 'bg-primary/10 text-primary p1-marke' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}
          data-testid={`nav-${kennung(item.label)}`}>
          <Icon className="w-[18px] h-[18px] flex-shrink-0" />
          <span className="flex-1 text-left">{item.label}</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${salesOpen || isSalesActive ? 'rotate-0' : '-rotate-90'}`} />
        </button>
        {(salesOpen || isSalesActive) && (
          <div className="ml-4 pl-3 border-l-2 border-border/60 mt-0.5 space-y-0.5">
            {item.children.map((child) => (
              <NavLink key={`step-${child.step}`} to={child.path} end onClick={handleNav} data-testid={`nav-step-${child.step}`}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  location.pathname === child.path ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border flex-shrink-0 ${
                  location.pathname === child.path ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground'}`}>
                  {child.step}
                </span>
                <span>{child.label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden backdrop-blur-[2px]" onClick={onMobileClose} />
      )}

      <aside
        data-testid="sidebar"
        className={`
          fixed left-0 top-0 h-full z-50 flex flex-col border-r border-border/70 transition-all duration-300
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${isCollapsed ? 'md:w-[68px]' : 'w-[260px] md:w-[240px]'}
        `}
        style={{ background: 'hsl(var(--sidebar-bg))' }}
      >
        {/* Logo */}
        <div className={`flex items-center h-14 md:h-16 px-4 border-b border-border ${isCollapsed ? 'md:justify-center' : 'justify-between'}`}>
          <div className="flex items-center gap-3">
            <img src="/logo-black.png" alt="Performance Center" className={`${isCollapsed ? 'h-7 w-7 object-contain' : 'h-8 object-contain max-w-[160px]'}`} />
          </div>
          {mobileOpen && (
            <button onClick={onMobileClose} className="md:hidden text-muted-foreground hover:text-foreground p-1">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Suche */}
        {!isCollapsed && (
          <div className="px-3 pt-3">
            <button onClick={() => setSuche(true)} data-testid="nav-suche"
              className="flex items-center gap-2 w-full rounded-xl bg-muted px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
              <Search className="w-4 h-4 flex-shrink-0" /><span className="flex-1 text-left">Seite suchen</span>
              <kbd className="rounded-md border border-border px-1.5 text-[10px] font-medium">⌘K</kbd>
            </button>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 overflow-y-auto">
          {isCollapsed ? (
            <div className="space-y-0.5">
              {[...feste, ...favoriten].map(item => symbol(item))}
              <div className="mx-2 my-2 border-t border-border/70" />
              {bereiche.map(s => {
                const SIcon = s.icon;
                return (
                  <Tooltip key={s.titel}>
                    <TooltipTrigger asChild>
                      <button onClick={() => { setOffen(s.titel); onToggle(); }} data-testid={`nav-bereich-${s.titel.toLowerCase()}`}
                        className={`flex items-center justify-center w-full px-2 py-2.5 rounded-xl text-sm transition-colors ${aktiverBereich === s.titel ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
                        <SIcon className="w-[18px] h-[18px] flex-shrink-0" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">{s.titel}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ) : (
            <>
              <div className="space-y-0.5">{feste.map(item => eintrag(item, 'nav', false))}</div>

              <p className={ueberschrift}>Meine Favoriten</p>
              <div className="space-y-0.5">
                {favoriten.map(item => eintrag(item, 'fav'))}
                {!favoriten.length && <p className="px-3 py-1 text-xs text-muted-foreground">Fahre über einen Punkt und klicke den Stern.</p>}
              </div>

              <p className={ueberschrift}>Bereiche</p>
              <div className="space-y-0.5">
                {bereiche.map(s => {
                  const SIcon = s.icon;
                  const auf = offen === s.titel;
                  return (
                    <div key={s.titel} data-testid={`nav-bereich-${s.titel.toLowerCase()}`}>
                      <button onClick={() => setOffen(auf ? null : s.titel)} aria-expanded={auf}
                        className={`flex items-center gap-3 w-full px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 hover:translate-x-0.5 hover:bg-accent ${aktiverBereich === s.titel ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                        <SIcon className={`w-[18px] h-[18px] flex-shrink-0 ${aktiverBereich === s.titel ? 'text-primary' : ''}`} />
                        <span className="flex-1 text-left">{s.titel}</span>
                        <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-300 ${auf ? 'rotate-90' : ''}`} />
                      </button>
                      <div className="p1-klappe" data-offen={auf} inert={!auf}>
                        <div>
                          <div className="ml-[21px] pl-2 border-l border-border/70 space-y-0.5 py-0.5">
                            {s.items.map(item => (item.children ? verkaufsrechner(item) : eintrag(item, 'nav')))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </nav>

        {/* Bottom */}
        <div className="border-t border-border p-2 space-y-1">
          {!isCollapsed && (
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2 px-3 py-1.5" role="group" aria-label="Akzentfarbe" data-testid="akzent-wahl">
              <span className="text-xs text-muted-foreground mr-auto">Farbe</span>
              {AKZENTE.map(a => (
                <button key={a.id} onClick={() => setAkzent(a.id)} title={a.name} aria-label={a.name} aria-pressed={akzent === a.id}
                  className={`h-5 w-5 rounded-full transition-transform duration-200 hover:scale-125 ${akzent === a.id ? 'ring-2 ring-offset-2 ring-offset-background' : ''}`}
                  style={{ background: `hsl(${a.h} ${a.s} ${a.l})`, '--tw-ring-color': `hsl(${a.h} ${a.s} ${a.l})` }} />
              ))}
            </div>
          )}
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleTheme}
                  data-testid="theme-toggle"
                  className="hidden md:flex items-center justify-center w-full px-2 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                >
                  {theme === 'dark' ? <Sun className="w-[18px] h-[18px] flex-shrink-0" /> : <Moon className="w-[18px] h-[18px] flex-shrink-0" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{theme === 'dark' ? 'Hellmodus' : 'Dunkelmodus'}</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              {theme === 'dark' ? <Sun className="w-[18px] h-[18px] flex-shrink-0" /> : <Moon className="w-[18px] h-[18px] flex-shrink-0" />}
              <span>{theme === 'dark' ? 'Hellmodus' : 'Dunkelmodus'}</span>
            </button>
          )}

          <DiktatEintrag eingeklappt={isCollapsed} />

          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onToggle}
                  data-testid="sidebar-toggle"
                  className="hidden md:flex items-center justify-center w-full px-2 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                >
                  <ChevronRight className="w-[18px] h-[18px] flex-shrink-0" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Erweitern</TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={onToggle}
              data-testid="sidebar-toggle"
              className="hidden md:flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-[18px] h-[18px] flex-shrink-0" />
              <span>Einklappen</span>
            </button>
          )}

          {!isCollapsed && user && (
            <div className="flex items-center gap-3 px-3 py-2 mt-1">
              <button
                onClick={() => setShowAccountDialog(true)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
                data-testid="account-security-trigger"
                title="Konto & Sicherheit"
              >
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">
                  {user.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user.role_name || user.role}</p>
                </div>
              </button>
              <button onClick={handleLogout} data-testid="logout-btn" className="text-muted-foreground hover:text-destructive transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
          <Dialog open={suche} onOpenChange={(o) => { setSuche(o); if (!o) setFrage(''); }}>
            <DialogContent className="max-w-md p-4">
              <DialogHeader><DialogTitle className="text-base">Seite suchen</DialogTitle></DialogHeader>
              <input autoFocus value={frage} onChange={e => setFrage(e.target.value)} data-testid="nav-suche-eingabe"
                onKeyDown={e => { if (e.key === 'Enter' && treffer[0]) gehe(treffer[0].path); }}
                placeholder="z. B. Rechnungslauf" aria-label="Seite suchen"
                className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring" />
              <div className="max-h-72 overflow-y-auto space-y-0.5">
                {treffer.slice(0, 40).map((i, n) => {
                  const Icon = i.icon;
                  return (
                    <button key={i.path} onClick={() => gehe(i.path)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-accent ${n === 0 && frage ? 'bg-accent' : ''}`}>
                      <Icon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />{i.label}
                    </button>
                  );
                })}
                {!treffer.length && <p className="px-3 py-2 text-sm text-muted-foreground">Keine Seite mit diesem Namen.</p>}
              </div>
            </DialogContent>
          </Dialog>
          <AccountSecurityDialog
            open={showAccountDialog}
            onClose={(changed) => { setShowAccountDialog(false); if (changed) checkAuth(); }}
          />
          {isCollapsed && user && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={handleLogout} data-testid="logout-btn-collapsed" className="hidden md:flex items-center justify-center w-full px-2 py-2.5 rounded-md text-muted-foreground hover:text-destructive transition-colors">
                  <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Abmelden</TooltipContent>
            </Tooltip>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
